import React, { useCallback, useEffect, useState } from 'react';

import type { DocNode } from '@atlaskit/adf-schema';

import type { AvailableSite } from '@atlassian/gic-anywhere';

import { SingleWorkCreateForm } from '../single-work-create-form/SingleWorkCreateForm';
import { useIssueCreateSidePanelActions } from '../../providers/IssueCreateSidePanelContextProvider';
import { SingleWorkCreateFormAiSupportedSection } from '../single-work-create-form/SingleWorkCreateFormAiSupportedSection';
import type { JiraProject } from '../../__types__/apiUtils';
import type { AiSectionProps } from '../../__types__/IssueCreateFormRenderer';

export interface IssueCreateFormRendererProps {
	onIssueCreateInit: () => void;
	onClose: () => void;
	summary?: string;
	descriptionAdf?: DocNode;
	setOverrideCloudIdForSitePicker?: (cloudId: string) => void;
	setOverrideProjectId?: (projectId: string) => void;
	isCreatedWithAi: boolean;
	aiSection: (props: AiSectionProps) => React.ReactNode;
	setShouldSwitchToGicAnywhere: (shouldSwitch: boolean) => void;
}

export const IssueCreateFormRenderer: React.FC<IssueCreateFormRendererProps> = ({
	onIssueCreateInit,
	onClose,
	summary = '',
	descriptionAdf,
	setOverrideCloudIdForSitePicker,
	setOverrideProjectId,
	isCreatedWithAi = false,
	aiSection,
	setShouldSwitchToGicAnywhere,
}) => {
	const [currentSummary, setCurrentSummary] = useState<string>(summary);
	const [currentDescription, setCurrentDescription] = useState<DocNode | undefined>(descriptionAdf);
	const [isDescriptionRequired, setIsDescriptionRequired] = useState(false);
	const actions = useIssueCreateSidePanelActions();

	useEffect(() => {
		onIssueCreateInit?.();
	}, [onIssueCreateInit]);

	const handleShouldUseEmbed = useCallback(
		(shouldUse: { selectedSite: AvailableSite | null; selectedProject: JiraProject | null }) => {
			if (shouldUse) {
				setShouldSwitchToGicAnywhere(true);
				if (shouldUse.selectedSite) {
					void setOverrideCloudIdForSitePicker?.(shouldUse.selectedSite.cloudId);
				}
				if (shouldUse.selectedProject) {
					void setOverrideProjectId?.(shouldUse.selectedProject.id);
				}
				if (currentSummary) {
					void actions.setSummary(currentSummary);
				}
				if (currentDescription) {
					void actions.setDescriptionAdf(currentDescription);
				}
			}
		},
		[
			currentSummary,
			currentDescription,
			actions,
			setOverrideCloudIdForSitePicker,
			setOverrideProjectId,
			setShouldSwitchToGicAnywhere,
		],
	);

	const handleSummaryChange = useCallback((value: string) => {
		setCurrentSummary(value);
	}, []);

	const handleDescriptionChange = useCallback((value: DocNode | undefined) => {
		setCurrentDescription(value);
	}, []);

	return (
		<SingleWorkCreateForm
			closePanel={onClose}
			currentSummary={currentSummary}
			currentDescription={currentDescription}
			onShouldUseEmbed={handleShouldUseEmbed}
			onDescriptionRequiredChange={setIsDescriptionRequired}
			aiSupportedSection={
				!isCreatedWithAi ? (
					<SingleWorkCreateFormAiSupportedSection
						initialSummary={summary}
						initialDescription={descriptionAdf}
						handleSummaryChange={handleSummaryChange}
						handleDescriptionChange={handleDescriptionChange}
						isDescriptionRequired={isDescriptionRequired}
						isCreatedWithAi={false}
					/>
				) : (
					aiSection({
						handleSummaryChange,
						handleDescriptionChange,
						isDescriptionRequired,
					})
				)
			}
		/>
	);
};
